import { RichEditor } from '@odo/shared-ui';
import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useResourcesConfig } from '../config/ResourcesConfigContext';
import AudienceField from './AudienceField';
import CategoryField from './CategoryField';
import PdfField from './PdfField';
import { MAX_PDF_BYTES, useResourceWrites, writeErrorMessage } from './useResourceWrites';
import {
    AUTHORING_LABEL,
    slugify,
    validateDraft,
    type AuthoringKind,
    type ResourceDraft
} from './types';

const INPUT_CLASS =
    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 disabled:bg-gray-50';

const HEADING: Record<AuthoringKind, { create: string; edit: string }> = {
    faq: { create: 'New question', edit: 'Edit question' },
    helpDocument: { create: 'New help document', edit: 'Edit help document' },
    video: { create: 'New video', edit: 'Edit video' }
};

interface FieldProps {
    label: string;
    hint?: string;
    /** 'group' renders a div: a contenteditable in a label steals focus on body clicks. */
    wrap?: 'label' | 'group';
    children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, hint, wrap = 'label', children }) => {
    const body = (
        <>
            <span className="text-xs font-semibold text-gray-700">{label}</span>
            {children}
            {hint && <span className="text-xs text-gray-400">{hint}</span>}
        </>
    );
    return wrap === 'label' ? (
        <label className="flex flex-col gap-2">{body}</label>
    ) : (
        <div className="flex flex-col gap-2">{body}</div>
    );
};

interface ResourceFormModalProps {
    draft: ResourceDraft;
    /** Categories already used by this content type, for the tag field. */
    suggestions: string[];
    onClose: () => void;
    /** Called after a successful write, before the modal closes. */
    onSaved: () => Promise<void>;
    onDelete?: () => void;
}

/** One form for all content types; create and edit are the same screen, so the
 * draft carries an optional id rather than the modal a mode. */
export const ResourceFormModal: React.FC<ResourceFormModalProps> = ({
    draft: initial,
    suggestions,
    onClose,
    onSaved,
    onDelete
}) => {
    const { authoring } = useResourcesConfig();
    const { save } = useResourceWrites();
    const [draft, setDraft] = useState<ResourceDraft>(initial);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isEdit = draft.id != null;
    const label = AUTHORING_LABEL[draft.kind];
    const heading = isEdit ? HEADING[draft.kind].edit : HEADING[draft.kind].create;

    const patch = (next: Partial<ResourceDraft>) =>
        setDraft((current) => ({ ...current, ...next }));

    const setTitle = (title: string) =>
        patch(
            // The slug follows the title until it is edited by hand, and never
            // on an existing row: changing a published slug changes its URL.
            draft.slugTouched || isEdit ? { title } : { title, slug: slugify(title) }
        );

    const pickPdf = (file: File | null) => {
        if (file && file.size > MAX_PDF_BYTES) {
            setError(
                `That PDF is larger than the ${Math.round(MAX_PDF_BYTES / 1024 / 1024)}MB limit.`
            );
            return;
        }
        setError(null);
        patch({ pdfFile: file });
    };

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        const invalid = validateDraft(draft);
        if (invalid) {
            setError(invalid);
            return;
        }

        setIsSaving(true);
        setError(null);
        try {
            await save(draft);
            await onSaved();
            onClose();
        } catch (err) {
            setError(writeErrorMessage(err, `Could not save this ${label}. Please try again.`));
        } finally {
            setIsSaving(false);
        }
    };

    const slugField = (
        <Field label="Slug" hint="Used in the URL. Lowercase words joined by hyphens.">
            <input
                value={draft.slug}
                onChange={(event) =>
                    patch({ slug: slugify(event.target.value), slugTouched: true })
                }
                disabled={isSaving}
                placeholder="bonding-protocol"
                className={INPUT_CLASS}
            />
        </Field>
    );

    const titleField = (
        <Field label="Title">
            <input
                value={draft.title}
                onChange={(event) => setTitle(event.target.value)}
                disabled={isSaving}
                className={INPUT_CLASS}
            />
        </Field>
    );

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"
            onClick={onClose}
        >
            <form
                onSubmit={submit}
                onClick={(event) => event.stopPropagation()}
                className="bg-white rounded-lg shadow-xl w-full max-w-[640px] max-h-[90vh] flex flex-col"
            >
                <div className="flex justify-between items-center px-6 pt-6 pb-4">
                    <h2 className="text-xl font-bold text-gray-900">{heading}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="text-gray-400 hover:text-gray-800 transition-colors"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 flex flex-col gap-4">
                    {draft.kind === 'faq' && (
                        <>
                            <Field label="Question">
                                <input
                                    value={draft.question}
                                    onChange={(event) => patch({ question: event.target.value })}
                                    disabled={isSaving}
                                    className={INPUT_CLASS}
                                />
                            </Field>
                            <Field
                                label="Answer"
                                wrap="group"
                                hint="Formatting shows on the FAQ page exactly as it looks here."
                            >
                                {/* No images: the answer column stores the document
                                    alone, with no companion table to own an upload. */}
                                <RichEditor
                                    value={draft.answer}
                                    onChange={(answer) => patch({ answer })}
                                    readOnly={isSaving}
                                    allowImages={false}
                                    autoFocus={false}
                                    minHeightPx={200}
                                    placeholder="Answer the question in a line or two…"
                                />
                            </Field>
                        </>
                    )}

                    {draft.kind === 'helpDocument' && (
                        <>
                            {titleField}
                            {slugField}
                            <Field
                                label="PDF"
                                // A label would forward its click to the hidden
                                // input as well, opening the picker twice.
                                wrap="group"
                                hint={
                                    draft.pdfFileId && !draft.pdfFile
                                        ? 'View, replace or remove the stored file — a help document always needs a PDF.'
                                        : `PDF only, up to ${Math.round(MAX_PDF_BYTES / 1024 / 1024)}MB.`
                                }
                            >
                                <PdfField
                                    file={draft.pdfFile}
                                    storedFileId={draft.pdfFileId}
                                    disabled={isSaving}
                                    onPick={pickPdf}
                                    onRemoveStored={() => patch({ pdfFileId: null })}
                                />
                            </Field>
                        </>
                    )}

                    {draft.kind === 'video' && (
                        <>
                            {titleField}
                            {slugField}
                            <Field label="Video URL" hint="The YouTube link practices will watch.">
                                <input
                                    value={draft.videoUrl}
                                    onChange={(event) => patch({ videoUrl: event.target.value })}
                                    disabled={isSaving}
                                    placeholder="https://www.youtube.com/watch?v=..."
                                    className={INPUT_CLASS}
                                />
                            </Field>
                            <Field label="Description" wrap="group">
                                <RichEditor
                                    value={draft.description}
                                    onChange={(description) => patch({ description })}
                                    readOnly={isSaving}
                                    allowImages={false}
                                    autoFocus={false}
                                    minHeightPx={140}
                                    placeholder="What this video covers…"
                                />
                            </Field>
                        </>
                    )}

                    <AudienceField
                        value={draft.audience}
                        onChange={(audience) => patch({ audience })}
                        disabled={isSaving}
                    />

                    <CategoryField
                        value={draft.category}
                        onChange={(category) => patch({ category })}
                        suggestions={suggestions}
                        disabled={isSaving}
                    />

                    {error && (
                        <p className="text-sm text-red-500" role="alert">
                            {error}
                        </p>
                    )}
                </div>

                <div className="flex justify-end items-center gap-3 px-6 py-5">
                    {isEdit && authoring.canDelete && onDelete && (
                        <button
                            type="button"
                            onClick={onDelete}
                            disabled={isSaving}
                            className="mr-auto text-sm font-semibold text-red-500 hover:text-red-600 transition-colors disabled:opacity-50"
                        >
                            Delete
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSaving}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isSaving}
                        className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? 'Saving…' : isEdit ? 'Save changes' : `Create ${label}`}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ResourceFormModal;
